import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { IoArrowBack, IoTimeOutline, IoBusinessOutline, IoHomeOutline, IoPeopleOutline } from 'react-icons/io5';
import moment from 'moment';
import 'moment/locale/es';
import './MiBitacoraAccesosScreen.css';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

moment.locale('es');

function MiBitacoraAccesosScreen() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [misAccesos, setMisAccesos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtroMes, setFiltroMes] = useState(moment().format('YYYY-MM'));

    useEffect(() => {
        if (!currentUser) return;

        const fetchMisAccesos = async () => {
            setLoading(true);
            try {
                // Get start and end of selected month
                const startDate = moment(filtroMes).startOf('month').toDate();
                const endDate = moment(filtroMes).endOf('month').toDate();

                // 1. Query parent's own accesses
                const qPropio = query(
                    collection(db, "accesos"),
                    where("residenteId", "==", currentUser.uid)
                );

                // 2. Query dependent accounts' accesses
                const qDependientes = query(
                    collection(db, "accesos"),
                    where("cuentaPadreId", "==", currentUser.uid)
                );

                const [snapPropio, snapDependientes] = await Promise.all([
                    getDocs(qPropio),
                    getDocs(qDependientes)
                ]);

                let accesosList = [];
                [snapPropio, snapDependientes].forEach(querySnapshot => {
                    querySnapshot.forEach((doc) => {
                        const data = doc.data();

                        // Excluir pases maestros multi-uso o ilimitados (solo mostrar logs individuales)
                        const isMasterMultiUsePass = (data.esIlimitado === true || data.usosMaximos > 1 || data.usosMaximos === -1) && !data.accesoOriginalId && !data.esEventoBitacora;
                        if (isMasterMultiUsePass && data.estatus === 'Activo') {
                            return;
                        }

                        // Mostrar SOLOS los que tienen estatus Completado, Usado o Denegado, o usos/evento
                        if (data.fecha && (data.estatus === 'Completado' || data.estatus === 'Usado' || data.estatus === 'Denegado' || data.usosActuales > 0 || data.esEventoBitacora)) {

                            // Parse fecha with fallback check
                            const dbDate = data.fecha.toDate ? data.fecha.toDate() : new Date(data.fecha);

                            // Perform JS date filter to emulate the range query
                            if (dbDate >= startDate && dbDate <= endDate) {
                                accesosList.push({
                                    ...data,
                                    id: doc.id,
                                    tipo: data.tipoDetallado || data.tipo || 'Visita',
                                    nombreVisita: data.nombreVisita || data.nombre || 'Visitante'
                                });
                            }
                        }
                    });
                });

                // Sort descending locally
                accesosList.sort((a, b) => {
                    const dateA = a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha);
                    const dateB = b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha);
                    return dateB - dateA;
                });

                setMisAccesos(accesosList);
            } catch (error) {
                console.error("Error al cargar mi bitácora:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchMisAccesos();
    }, [currentUser, filtroMes]);

    const getIconForType = (tipo) => {
        const lowerTipo = (tipo || '').toLowerCase();
        if (lowerTipo.includes('proveedor') || lowerTipo.includes('servicio')) return <IoBusinessOutline />;
        if (lowerTipo.includes('residente')) return <IoHomeOutline />;
        if (lowerTipo.includes('familiar')) return <IoHomeOutline />;
        return <IoPeopleOutline />;
    };

    const getBadgeClass = (tipo) => {
        const lower = (tipo || '').toLowerCase();
        if (lower.includes('proveedor') || lower.includes('servicio')) return 'servicio';
        if (lower.includes('residente')) return 'residente';
        return 'visita';
    };

    return (
        <>
            <KondoBrandHeader showBack={false} />
            <div className="mi-bitacora-container">
                <div className="bitacora-fixed-wrapper">
                    <div className="bitacora-banner-compact">
                        <button onClick={() => navigate(-1)} className="bitacora-back-btn glass" title="Volver">
                            <IoArrowBack size={24} />
                        </button>
                        <div className="bitacora-banner-content">
                            <h2>Mi Historial de Accesos</h2>
                            <p>Registro de tus visitas y servicios que ingresaron al fraccionamiento</p>
                        </div>
                        <div className="bitacora-banner-icon">
                            <IoTimeOutline />
                        </div>
                    </div>
                </div>

                <div className="mi-bitacora-main">
                    <div className="month-filter-card">
                        <label>Ver accesos de:</label>
                        <input
                            type="month"
                            value={filtroMes}
                            onChange={(e) => setFiltroMes(e.target.value)}
                            className="month-picker"
                        />
                    </div>

                    <div className="accesos-list">
                        {loading && <div className="loading-state">Cargando tus visitas...</div>}

                        {!loading && misAccesos.length === 0 && (
                            <div className="empty-state">
                                <IoTimeOutline size={48} style={{ opacity: 0.3, marginBottom: '10px' }} />
                                <p>No tienes registros de acceso en este mes.</p>
                            </div>
                        )}

                        {!loading && misAccesos.map((acceso) => {
                            const dateObj = acceso.fecha?.toDate ? acceso.fecha.toDate() : new Date(acceso.fecha);
                            const isDenied = acceso.estatus === 'Denegado';
                            return (
                                <div key={acceso.id} className={`acceso-card ${isDenied ? 'denied-card' : ''}`}>
                                    <div className={`acceso-icon-frame ${isDenied ? 'denied-icon' : ''}`}>
                                        {getIconForType(acceso.tipo)}
                                    </div>
                                    <div className="acceso-info">
                                        <h4>{acceso.nombreVisita || acceso.visitante || 'Visitante Desconocido'}</h4>
                                        <div className="acceso-meta">
                                            {isDenied ? (
                                                <span className="badge denegado">Acceso Denegado</span>
                                            ) : (
                                                <span className={`badge ${getBadgeClass(acceso.tipo)}`}>
                                                    {acceso.tipo || 'Visita'}
                                                </span>
                                            )}
                                            <span className="acceso-date">
                                                {moment(dateObj).format('DD MMM')} a las {moment(dateObj).format('hh:mm A')}
                                            </span>
                                        </div>
                                    </div>
                                    {acceso.vigilanteName && (
                                        <div className="acceso-guard" title={`Registrado por: ${acceso.vigilanteName}`}>
                                            Ingresó Caseta
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
            <KondoBottomNav activeRoute="/accesos" />
        </>
    );
}

export default MiBitacoraAccesosScreen;
